import clsx from 'clsx';
import { FunctionComponent, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';

import { noopFunction } from '@vestico/utils';

import { ClientImageSourceSet, CropHint } from '@vestico/model';

import { useElementSize } from '../hooks';
import { useCropHintStyles } from '../hooks/useCropHintStyles';
import style from './ImageViewerMobile.module.css';
import { Spinner, SpinnerPositioning, SpinnerSize } from './Spinner';
import { useImageSources } from './useImageSources';

export interface ImageViewerMobileProps {
  url: string;
  srcSet: ClientImageSourceSet;
  thumbURL?: string;
  cropHint?: CropHint;
  onLoad?: () => void;
  onZoomStart?: () => void;
  onZoomEnd?: () => void;
  enableZoom: boolean;
}

const ZOOM_ENABLE_TIMEOUT = 500;

export const ImageViewerMobile: FunctionComponent<ImageViewerMobileProps> = ({
  url,
  srcSet,
  thumbURL,
  onLoad,
  onZoomStart,
  onZoomEnd,
  enableZoom,
  cropHint,
}) => {
  const [loadedThumbImageURL, setLoadedThumbImageURL] = useState<undefined | string>(undefined);
  const [loadedMainImageURL, setLoadedMainImageURL] = useState<undefined | string>(undefined);

  const thumbImgElement = useRef(new Image());

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [mediaAspectRatio, setMediaAspectRatio] = useState<number | undefined>(undefined);
  const containerDimensions = useElementSize(rootRef);

  const { css: imageCSS } = useCropHintStyles({ cropHint, mediaAspectRatio, containerDimensions });

  const onThumbLoad = useCallback(
    (e: Event) => {
      const { width, height } = e.currentTarget as HTMLImageElement;
      if (width && height) {
        setMediaAspectRatio(width / height);
      }
      setLoadedThumbImageURL(thumbURL);
    },
    [thumbURL]
  );

  const onLoadMainImage = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const loadedURL = event.currentTarget.src;
      const loadedMainImage = loadedURL === url;

      if (!mediaAspectRatio) {
        const { width, height } = event.currentTarget;
        if (width && height) {
          setMediaAspectRatio(width / height);
        }
      }
      if (loadedMainImage) {
        setLoadedMainImageURL(url);
        if (onLoad) {
          onLoad();
        }
      }
    },
    [mediaAspectRatio, onLoad, url]
  );

  useLayoutEffect(() => {
    const hasAlreadyLoadedMain = loadedMainImageURL === url;
    const hasAlreadyLoadedThumb = loadedThumbImageURL === thumbURL;

    if (thumbURL && !hasAlreadyLoadedThumb) {
      setLoadedThumbImageURL(undefined);
      setMediaAspectRatio(undefined);
      const img = thumbImgElement.current;
      img.src = thumbURL;
      img.decoding = 'async';
      img.onload = onThumbLoad;
    }

    if (!hasAlreadyLoadedMain) {
      setLoadedMainImageURL(undefined);
    }
  }, [loadedMainImageURL, loadedThumbImageURL, onLoad, onThumbLoad, thumbURL, url]);

  const [resetZoom, setResetZoom] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const pinchZoomRef = useRef<QuickPinchZoom>(null);
  const [scale, setScale] = useState<number>(1.0);

  useEffect(() => {
    if (!resetZoom) {
      return;
    }
    if (onZoomEnd) {
      onZoomEnd();
    }

    setResetZoom(false);
    if (scale > 0) {
      const pinchZoom = pinchZoomRef.current;
      if (pinchZoom) {
        pinchZoom.scaleTo({ x: 0.0, y: 0.0, scale: 1.0 });
      }
    }
  }, [onZoomEnd, resetZoom, scale]);

  const [allowZoom, setAllowZoom] = useState(false);

  const onUpdate = useCallback(
    ({ x, y, scale: newScale }: { x: number; y: number; scale: number }) => {
      const { current: img } = imgRef;

      if (!allowZoom) {
        return;
      }

      setScale(newScale);

      if (img) {
        const value = make3dTransformValue({ x, y, scale: newScale });
        img.style.setProperty('transform', value);
      }
    },
    [allowZoom, imgRef]
  );

  const isLoadingThumb = useMemo(() => !!thumbURL && loadedMainImageURL !== url, [thumbURL, loadedMainImageURL, url]);
  const imgSrc = loadedMainImageURL ?? loadedThumbImageURL;

  const loadingImageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const { current: loadingImage } = loadingImageRef;
    if (!loadingImage) {
      return noopFunction;
    }

    const id = requestAnimationFrame(() => {
      loadingImage.classList.toggle(clsx(style.loading), isLoadingThumb);
      loadingImage.classList.toggle(clsx(style.loaded), !isLoadingThumb);
    });
    return () => cancelAnimationFrame(id);
  }, [isLoadingThumb, loadedMainImageURL, loadedThumbImageURL]);

  // We need to add a timeout to allow transitioning from img to QuickPinchZoom without a flicker
  // Flicker is caused by QuickPinchZoom
  useEffect(() => {
    // Allow zoom only after timeout and when thumb is not loading anymore
    if (enableZoom && !isLoadingThumb) {
      const timeout = setTimeout(() => {
        setAllowZoom(enableZoom);
      }, ZOOM_ENABLE_TIMEOUT);
      return () => clearTimeout(timeout);
    }

    setAllowZoom(false);
    return noopFunction;
  }, [enableZoom, isLoadingThumb]);

  const [showLoadingImage, setShowLoadingImage] = useState<boolean>(true);
  useEffect(() => {
    if (!allowZoom) {
      setShowLoadingImage(true);
      return noopFunction;
    }

    const timeout = setTimeout(() => {
      setShowLoadingImage(false);
    }, ZOOM_ENABLE_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [allowZoom]);

  const handleZoomEnd = useCallback(() => setResetZoom(true), []);

  const didLoadThumb = useMemo(() => loadedThumbImageURL === thumbURL, [loadedThumbImageURL, thumbURL]);

  const { sources, onImageError: onShadowImageError } = useImageSources({ url, srcSet, mediaAspectRatio, didLoadThumb, containerDimensions });

  const shadowMainImageMarkup =
    didLoadThumb && !loadedMainImageURL ? (
      <picture>
        {sources}
        {/* skipcq JS-W1015 */}
        <img
          src={url}
          className={clsx(style.image, style.shadow)}
          draggable={false}
          onLoad={onLoadMainImage}
          onError={onShadowImageError}
          style={imageCSS}
          alt=""
          aria-hidden="true"
        />
      </picture>
    ) : null;

  const mainImageMarkup =
    loadedThumbImageURL || loadedMainImageURL ? (
      // skipcq JS-W1015
      <picture>
        {!isLoadingThumb ? sources : undefined}
        <img src={imgSrc} className={clsx(style.image)} draggable={false} alt="" ref={loadingImageRef} style={imageCSS} />
      </picture>
    ) : null;

  const spinnerMarkup =
    !loadedMainImageURL && !loadedThumbImageURL ? (
      <div className={clsx(style.spinnerContainer)}>
        <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.transform} />
      </div>
    ) : null;

  return (
    <div className={clsx(style.root)} ref={rootRef}>
      {spinnerMarkup}
      {showLoadingImage ? mainImageMarkup : null}
      {allowZoom ? (
        <div className={clsx(style.zoom)}>
          <QuickPinchZoom
            onUpdate={onUpdate}
            draggableUnZoomed={false}
            tapZoomFactor={0.0}
            onZoomStart={onZoomStart}
            onZoomEnd={handleZoomEnd}
            onDragEnd={handleZoomEnd}
            ref={pinchZoomRef}
          >
            <picture style={{ display: 'block', height: '100%', width: '100%' }} ref={imgRef}>
              {!isLoadingThumb ? sources : undefined}
              {/* skipcq JS-W1015 */}
              <img src={imgSrc} className={clsx(style.image)} draggable={false} alt="" style={imageCSS} />
            </picture>
          </QuickPinchZoom>
        </div>
      ) : null}
      {shadowMainImageMarkup}
    </div>
  );
};
